import { create } from 'zustand';
import { immer } from 'zustand/middleware/immer';

import type { ListGroupsAdministrationParams, ListUsersAdministrationParams } from '@/modules/api/codegen';
import { omitUndefined } from '@/services/utilities/object';

import { UserAdministrationType } from '../constants';

// Filters
export type UserAdministrationFilters = Pick<ListUsersAdministrationParams, 'status' | 'role' | 'groups' | 'search'>;
export type GroupAdministrationFilters = Pick<ListGroupsAdministrationParams, 'status' | 'search'>;
type SetFiltersParams =
    | { type: UserAdministrationType.Users; filters: UserAdministrationFilters }
    | { type: UserAdministrationType.Groups; filters: GroupAdministrationFilters };

// Pagination
export type UserAdministrationPagination = Pick<ListUsersAdministrationParams, 'page' | 'limit'>;
export type GroupAdministrationPagination = Pick<ListGroupsAdministrationParams, 'page' | 'limit'>;
type SetPaginationParams =
    | { type: UserAdministrationType.Users; pagination: UserAdministrationPagination }
    | { type: UserAdministrationType.Groups; pagination: GroupAdministrationPagination };

// Sort
export type UserAdministrationSort = Pick<ListUsersAdministrationParams, 'orderBy' | 'orderDirection'>;
export type GroupAdministrationSort = Pick<ListGroupsAdministrationParams, 'orderBy' | 'orderDirection'>;
type SetSortParams =
    | { type: UserAdministrationType.Users; sort: UserAdministrationSort }
    | { type: UserAdministrationType.Groups; sort: GroupAdministrationSort };

// Selected Rows
export type SetSelectedRowsParams = { type: UserAdministrationType; selectedRowsKeys: React.Key[] };

export interface UserAdministrationStore {
    filters: {
        users: UserAdministrationFilters;
        groups: GroupAdministrationFilters;
    };
    pagination: {
        users: UserAdministrationPagination;
        groups: GroupAdministrationPagination;
    };
    sort: {
        users: UserAdministrationSort;
        groups: GroupAdministrationSort;
    };
    selectedRows: {
        users: SetSelectedRowsParams['selectedRowsKeys'];
        groups: SetSelectedRowsParams['selectedRowsKeys'];
    };
    setFilters: (params: SetFiltersParams) => void;
    setPagination: (params: SetPaginationParams) => void;
    setSort: (params: SetSortParams) => void;
    resetSort: (type: UserAdministrationType) => void;
    setSelectedRows: (params: SetSelectedRowsParams) => void;
}

const initialState = {
    filters: {
        users: {},
        groups: {},
    },
    pagination: {
        users: { page: 1, limit: 10 },
        groups: { page: 1, limit: 10 },
    },
    sort: { users: {}, groups: {} },
    selectedRows: { users: [], groups: [] },
} as const satisfies Partial<UserAdministrationStore>;

export const useUserAdministrationStore = create<UserAdministrationStore>()(
    immer(set => ({
        ...initialState,

        setFilters: (params: SetFiltersParams) =>
            set(state => {
                state.filters[params.type] = omitUndefined(params.filters);
            }),
        setPagination: (params: SetPaginationParams) =>
            set(state => {
                state.pagination[params.type] = omitUndefined(params.pagination);
            }),
        setSort: (params: SetSortParams) =>
            set(state => {
                if (params.type === UserAdministrationType.Users) {
                    state.sort.users = omitUndefined(params.sort);
                } else {
                    state.sort.groups = omitUndefined(params.sort);
                }
            }),
        resetSort: (type: UserAdministrationType) =>
            set(state => {
                state.sort[type] = initialState.sort[type];
            }),
        setSelectedRows: (params: SetSelectedRowsParams) =>
            set(state => {
                state.selectedRows[params.type] = params.selectedRowsKeys;
            }),
    })),
);
